/**
 * `OnchainActivityPanel` — the Overview blockchain-activity widget.
 *
 * Shows recent cross-chain activity as either a virtualised **Table**
 * (`@authe/tx-table`) or an interactive multichain **Graph** (`@authe/tx-graph`),
 * with a Live / Auto / Off update control (`@authe/ui-core`). View + live-mode
 * choices persist via the shared `DisplayPrefsStore`. All field names,
 * formatting, view-mode and live-mode vocabulary come from `@authe/fe-commons` +
 * the lower packages, so this surface stays consistent with the rest of the app.
 *
 * This is the *onchain* surface — recent blockchain transactions + the
 * interaction graph. It is intentionally separate from the in-app event inbox at
 * `/activity` (`@authe/notification-center`), which tracks app/collaboration
 * events. Two distinct feeds, clearly named.
 */
import * as React from "react";
import { Link } from "@tanstack/react-router";
import { RefreshCw, Share2, Table2, Wallet as WalletIcon } from "lucide-react";
import {
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  LiveModeControl,
  ViewModeToggle,
  cn,
  useDisplayPref,
} from "@authe/ui-core";
import { TransactionTable, type TxRow } from "@authe/tx-table";
import {
  freezeCatalogSnapshotFromNodes,
  type GraphShareState,
  type TxGraphNode,
} from "@authe/tx-graph";
import { AccessWarningBanner, type AccessWarning } from "@authe/saved-views";
import {
  ACTIVITY_VIEWS,
  type ActivityView,
  type GraphViewMode,
  type LiveMode,
} from "@authe/fe-commons/display";
import { ROUTES } from "@/config";
import { displayPrefs } from "@/app/prefs";
import { isStreamingConfigured } from "@/app/streaming";
import { GraphSavedViewsBar } from "@/features/graphs/GraphSavedViewsBar";
import { FeedGraph, useGraphViewModePref } from "@/features/graphs/FeedGraph";
import { useOnchainActivity } from "./useOnchainActivity";
import { OnchainLiveFeed } from "./OnchainLiveFeed";

import "@xyflow/react/dist/style.css";
import { useSubjectOpener } from "@/features/inspector";

const VIEW_LABELS: Record<ActivityView, React.ReactNode> = { table: "Table", graph: "Graph" };
const VIEW_ICONS: Record<ActivityView, React.ReactNode> = {
  table: <Table2 className="h-3.5 w-3.5" />,
  graph: <Share2 className="h-3.5 w-3.5" />,
};
const VIEW_OPTIONS = ACTIVITY_VIEWS.map((value) => ({
  value,
  label: VIEW_LABELS[value],
  icon: VIEW_ICONS[value],
}));

/** Pull a bare EVM address out of a chain-scoped node id (`chain#0x…`). */
function addressFromNodeId(id: string): string | undefined {
  const last = id.includes("#") ? id.slice(id.lastIndexOf("#") + 1) : id;
  return /^0x[0-9a-fA-F]{40}$/.test(last) ? last : undefined;
}

const GRAPH_HEIGHT = "h-[440px]";

export function OnchainActivityPanel(): React.ReactElement {
  const opener = useSubjectOpener();
  const liveAvailable = isStreamingConfigured;

  const [view, setView] = useDisplayPref<ActivityView>(
    () => displayPrefs.getActivityView(),
    (v) => displayPrefs.setActivityView(v),
  );
  const [liveMode, setLiveMode] = useDisplayPref<LiveMode>(
    () => displayPrefs.getLiveMode(),
    (v) => displayPrefs.setLiveMode(v),
  );
  // Honour the stored choice, but never run Live without an endpoint.
  const effectiveMode: LiveMode = liveMode === "live" && !liveAvailable ? "interval" : liveMode;

  // Overlays only while the graph is the view on screen — it is the only one
  // that draws them, and the only one that mints a share link from them.
  const feed = useOnchainActivity({
    liveMode: effectiveMode,
    resolveOverlays: view === "graph",
  });
  const liveActive = liveAvailable && effectiveMode === "live";

  // Graph layout mode is lifted here so the saved-views bar can snapshot it and
  // the canvas can render either the live feed or an applied saved snapshot.
  const [graphMode, setGraphMode] = useGraphViewModePref();

  // A saved graph view, when applied, replaces the live feed on the canvas;
  // resetting it (`null`) returns to the live graph.
  const [appliedGraph, setAppliedGraph] = React.useState<GraphShareState | null>(null);
  /** The rendered canvas box, so the views bar's export menu can offer the PNG. */
  const canvasRef = React.useRef<HTMLDivElement | null>(null);
  const [warnings, setWarnings] = React.useState<AccessWarning[]>([]);

  // The overlay the live graph is being READ with, frozen alongside it — the
  // element-level counterpart of what `SceneGraphView` does for a scene.
  //
  // This is the only place that can answer: by the time a saved view reaches the
  // share dialog the resolver is long gone, which is why an `owner_snapshot`
  // link minted on this surface used to resolve to `{}` and quietly serve the
  // reader's own catalog instead of the owner's labels, entities and risk.
  const catalogSnapshot = React.useMemo(
    () => freezeCatalogSnapshotFromNodes(feed.graph.nodes, feed.resolveOverlay),
    [feed.graph.nodes, feed.resolveOverlay],
  );

  // What the "Save as view" action snapshots: the applied view if one is open,
  // otherwise the current live feed graph + layout.
  //
  // An APPLIED view is handed back unchanged, deliberately. Its frozen catalog
  // lives on the view row and is not in the payload that came back, so stamping
  // this reader's overlay onto a re-save would replace the owner's accusations
  // with whoever happened to open it. Absent leaves the stored one alone.
  const currentGraph = React.useMemo<GraphShareState>(
    () =>
      appliedGraph ?? {
        viewMode: graphMode,
        nodes: feed.graph.nodes,
        edges: feed.graph.edges,
        ...(catalogSnapshot ? { catalog: catalogSnapshot } : {}),
      },
    [appliedGraph, graphMode, feed.graph.nodes, feed.graph.edges, catalogSnapshot],
  );

  const onRowClick = React.useCallback(
    (row: TxRow) => opener.openTransaction(row),
    [opener],
  );

  const onNodeClick = React.useCallback(
    (node: TxGraphNode) => {
      const address = addressFromNodeId(node.id);
      if (address) opener.openAddress(address);
    },
    [opener],
  );

  return (
    <Card className="mt-6">
      <CardHeader className="flex-row flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <CardTitle>{feed.hasWallet ? "Your onchain activity" : "Live blockchain activity"}</CardTitle>
          <CardDescription>
            {feed.hasWallet
              ? "Recent cross-chain history for your connected wallets, discovered across their active chains."
              : "The latest cross-chain transactions. Connect a wallet to see your own activity."}
          </CardDescription>
        </div>

        <div className="flex shrink-0 flex-wrap items-center gap-2">
          {liveActive && (
            <OnchainLiveFeed
              enabled
              onRows={feed.setStreamedRows}
              count={feed.streamedCount}
              addresses={feed.addresses}
            />
          )}
          <LiveModeControl mode={liveMode} onChange={setLiveMode} liveAvailable={liveAvailable} size="sm" />
          <ViewModeToggle<ActivityView>
            aria-label="Activity view"
            options={VIEW_OPTIONS}
            value={view}
            onChange={setView}
            size="sm"
          />
          {view === "graph" && (
            // Mounted by hand because this surface draws node/edge elements
            // rather than a `GraphScene`: applying a view swaps ITS payload, which
            // only the host can do. Scene surfaces get the same bar from
            // `DashboardGraphDefaults.renderViewsBar` instead of mounting one.
            <GraphSavedViewsBar
              graphId="overview.activity"
              currentGraph={currentGraph}
              onApplyGraph={setAppliedGraph}
              onWarnings={setWarnings}
              saveDisabled={!appliedGraph && feed.graph.nodes.length === 0}
              // So the bar's export menu can offer the picture too.
              canvasRef={canvasRef}
            />
          )}
          <Button
            type="button"
            size="icon"
            variant="ghost"
            className="h-7 w-7"
            onClick={() => feed.refetch()}
            disabled={feed.isFetching}
            aria-label="Refresh activity"
            title="Refresh"
          >
            <RefreshCw className={cn("h-3.5 w-3.5", feed.isFetching && "animate-spin")} />
          </Button>
          {!feed.hasWallet && (
            <Button asChild size="sm" variant="outline">
              <Link to={ROUTES.wallet}>
                <WalletIcon className="h-4 w-4" />
                Connect wallet
              </Link>
            </Button>
          )}
        </div>
      </CardHeader>

      <CardContent>
        {view === "table" ? (
          <>
            <TransactionTable
              rows={feed.rows}
              isLoading={feed.isLoading}
              isFetching={feed.isFetching}
              error={feed.error}
              onRetry={feed.refetch}
              onRowClick={onRowClick}
              enableToolbar={false}
              density="compact"
              height={GRAPH_HEIGHT}
              emptyTitle="No recent activity"
              emptyDescription={
                feed.hasWallet
                  ? "We couldn't find recent transactions for the connected wallets."
                  : "No transactions returned from the preview chains right now."
              }
            />
            {/* Only the wallet feed pages; the no-wallet preview is each chain's
                latest block, which has no "next". */}
            {feed.hasMore && (
              <div className="mt-3 flex justify-center">
                <Button
                  size="sm"
                  variant="outline"
                  onClick={feed.loadMore}
                  disabled={feed.isLoadingMore}
                >
                  {feed.isLoadingMore ? "Loading…" : "Load more"}
                </Button>
              </div>
            )}
          </>
        ) : (
          <>
            <AccessWarningBanner warnings={warnings} className="mb-3" />
            <OnchainGraph
              feed={feed}
              onNodeClick={onNodeClick}
              hasWallet={feed.hasWallet}
              appliedGraph={appliedGraph}
              graphMode={graphMode}
              onGraphModeChange={setGraphMode}
              onResetToLive={() => setAppliedGraph(null)}
              canvasRef={canvasRef}
            />
          </>
        )}
      </CardContent>
    </Card>
  );
}

interface OnchainGraphProps {
  feed: ReturnType<typeof useOnchainActivity>;
  onNodeClick: (node: TxGraphNode) => void;
  hasWallet: boolean;
  /** When set, render this saved snapshot instead of the live feed graph. */
  appliedGraph: GraphShareState | null;
  graphMode: GraphViewMode;
  onGraphModeChange: (mode: GraphViewMode) => void;
  onResetToLive: () => void;
  /** Filled with the canvas box, for the export menu's PNG (owned by the panel). */
  canvasRef: React.RefObject<HTMLDivElement | null>;
}

// Lightweight React Flow layouts only. The heavy "force" engine needs the
// optional `react-force-graph-2d` peer, which the dashboard doesn't install, so
// it's intentionally left out of the Overview's switcher.

function OnchainGraph({
  feed,
  onNodeClick,
  hasWallet,
  appliedGraph,
  graphMode,
  onGraphModeChange,
  onResetToLive,
  canvasRef,
}: OnchainGraphProps): React.ReactElement {
  const showingSnapshot = appliedGraph != null;
  const nodes = showingSnapshot ? appliedGraph.nodes ?? [] : feed.graph.nodes;
  const edges = showingSnapshot ? appliedGraph.edges ?? [] : feed.graph.edges;
  const mode = showingSnapshot ? appliedGraph.viewMode ?? graphMode : graphMode;

  // The four states, the box and the layout toggle are `<FeedGraph>`'s — the
  // same surface the transactions page's Graph tab draws. What is this panel's
  // own: a saved view can stand in for the live feed, which decides the data,
  // silences the feed's loading/error states, and says so above the canvas.
  return (
    <FeedGraph
      nodes={nodes}
      edges={edges}
      heightClassName={GRAPH_HEIGHT}
      isLoading={!showingSnapshot && feed.isLoading}
      error={showingSnapshot ? undefined : feed.error}
      onRetry={feed.refetch}
      onNodeClick={onNodeClick}
      mode={mode}
      onModeChange={onGraphModeChange}
      boxRef={canvasRef}
      emptyTitle={showingSnapshot ? "Empty saved view" : "No relationships to graph"}
      emptyDescription={
        showingSnapshot
          ? "This saved view has no graph payload. Reset to see live activity."
          : hasWallet
            ? "No cross-chain interactions found for the connected wallets yet."
            : "No interaction graph returned from the preview chains right now."
      }
      canvasHeader={
        showingSnapshot ? (
          <div className="flex items-center justify-between rounded-md border border-dashed bg-muted/40 px-3 py-1.5 text-xs text-muted-foreground">
            <span>Viewing a saved graph snapshot — live updates are paused.</span>
            <Button type="button" size="sm" variant="ghost" className="h-6" onClick={onResetToLive}>
              Reset to live
            </Button>
          </div>
        ) : undefined
      }
    />
  );
}
